import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { PinInput } from "@/components/PinInput";
import { inputCls } from "./shared";

const EMPTY = { current: "", new_pin: "", confirm_pin: "" };

export function AdminPinPanel({ pinEnabled, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const setPin = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_pin.length !== 7) { toast.error("PIN must be exactly 7 digits."); return; }
    if (form.new_pin !== form.confirm_pin) { toast.error("PINs do not match."); return; }
    setLoading(true);
    try {
      const r = pinEnabled
        ? await api.post("/auth/pin/change", { current_pin: form.current, new_pin: form.new_pin, confirm_pin: form.confirm_pin })
        : await api.post("/auth/pin/set", { current_password: form.current, new_pin: form.new_pin, confirm_pin: form.confirm_pin });
      toast.success(r.data.message);
      setForm(EMPTY);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not update the PIN.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-pin-panel">
      <div className="flex items-center gap-2 mb-1">
        <p className="text-sm font-bold text-navy">Admin PIN</p>
        <span className={`text-[11px] font-bold rounded-full border px-2.5 py-0.5 ${pinEnabled ? "bg-[#ECFDF3] text-[#15803D] border-[#BBE5C8]" : "bg-cream-dark text-slate-500 border-sand"}`} data-testid="pin-status">
          {pinEnabled ? "PIN sign-in on" : "Not set up"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        {pinEnabled
          ? "Sign in with your 7-digit PIN. To change it, enter your current PIN and the new one twice."
          : "Set a 7-digit PIN to sign in faster. Your password keeps working and is still used for recovery and sensitive changes."}
      </p>
      <form onSubmit={submit} className="grid gap-3 sm:max-w-md" data-testid="pin-form">
        <label className="block">
          <span className="block text-xs font-bold text-navy mb-1">{pinEnabled ? "Current PIN" : "Current password"}</span>
          {pinEnabled
            ? <PinInput id="pin-current" testid="current-pin-input" className={inputCls} value={form.current} onChange={setPin("current")} />
            : <PasswordInput id="pin-password" testid="pin-current-password-input" className={inputCls} value={form.current} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))} autoComplete="current-password" />}
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-navy mb-1">New 7-digit PIN</span>
          <PinInput id="pin-new" testid="new-pin-input" className={inputCls} value={form.new_pin} onChange={setPin("new_pin")} />
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-navy mb-1">Confirm new PIN</span>
          <PinInput id="pin-confirm" testid="confirm-pin-input" className={inputCls} value={form.confirm_pin} onChange={setPin("confirm_pin")} />
        </label>
        <button type="submit" disabled={loading} data-testid="pin-submit-button" className="min-h-[46px] rounded-xl bg-navy text-cream font-bold text-sm px-6 hover:bg-navy-light transition-colors inline-flex items-center justify-center gap-2 sm:w-fit disabled:opacity-60">
          <KeyRound className="w-4 h-4" /> {loading ? "Saving…" : pinEnabled ? "Change PIN" : "Set Up PIN"}
        </button>
      </form>
    </div>
  );
}
